import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import type { Credential } from "../../api/CertRoadmap/types";
import Spinner from "../Shared/Spinner";
import {
  catalogCategories,
  filterAndSortCredentials,
  isCertificateSort,
  mergeCatalogCredentials,
  type CertificateSort,
  type DifficultyScore,
  type LibraryCredential,
} from "./catalog";
import { credentialSegments } from "./config";
import CredentialLibraryCard from "./CredentialLibraryCard";
import CredentialLibraryFilters from "./CredentialLibraryFilters";
import CredentialVideoModal from "./CredentialVideoModal";
import type { ActiveClip, RoadmapLanguage } from "./types";

type CredentialLibraryProps = {
  credentials: Credential[];
  isLoading: boolean;
  language: RoadmapLanguage;
};

const validCategoryIds = new Set(
  catalogCategories.map((category) => category.id)
);
const validDifficultyScores = new Set<DifficultyScore>([1, 2, 3, 4, 5]);

const CredentialLibrary = ({
  credentials,
  isLoading,
  language,
}: CredentialLibraryProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null);
  const [flippedCards, setFlippedCards] = useState<string[]>([]);
  const [activeClip, setActiveClip] = useState<ActiveClip | null>(null);
  const text =
    language === "ar"
      ? {
          eyebrow: "الشهادات المهنية",
          title: "استكشف جميع الشهادات في المسار.",
          count: (count: number) => `${count} شهادة`,
          previous: "الشهادات السابقة",
          next: "الشهادات التالية",
          unavailable: "مقطع الفيديو غير متاح لهذه الشهادة حاليًا.",
          empty: "لا توجد شهادات تطابق الفلاتر المختارة.",
          clear: "مسح الفلاتر",
        }
      : {
          eyebrow: "Professional Certifications",
          title: "Explore every certification in the path.",
          count: (count: number) =>
            `${count} ${count === 1 ? "certification" : "certifications"}`,
          previous: "Previous certifications",
          next: "Next certifications",
          unavailable: "Video segment not available for this credential yet.",
          empty: "No certifications match the selected filters.",
          clear: "Clear filters",
        };

  const selectedCategoryIds = useMemo(() => {
    const categoryParam = searchParams.get("category");
    if (!categoryParam) return [];

    return [...new Set(categoryParam.split(","))].filter((categoryId) =>
      validCategoryIds.has(categoryId)
    );
  }, [searchParams]);

  const selectedDifficultyScores = useMemo(() => {
    const difficultyParam = searchParams.get("difficulty");
    if (!difficultyParam) return [];

    return [...new Set(difficultyParam.split(",").map(Number))].filter(
      (score): score is DifficultyScore =>
        validDifficultyScores.has(score as DifficultyScore)
    );
  }, [searchParams]);

  const sortParam = searchParams.get("sort");
  const selectedSort: CertificateSort = isCertificateSort(sortParam)
    ? sortParam
    : "recommended";

  const catalogCredentials = useMemo(
    () => mergeCatalogCredentials(credentials),
    [credentials]
  );

  const visibleCredentials = useMemo(
    () =>
      filterAndSortCredentials(
        catalogCredentials,
        selectedCategoryIds,
        selectedDifficultyScores,
        selectedSort
      ),
    [
      catalogCredentials,
      selectedCategoryIds,
      selectedDifficultyScores,
      selectedSort,
    ]
  );

  const updateUrlState = ({
    categoryIds = selectedCategoryIds,
    difficultyScores = selectedDifficultyScores,
    sort = selectedSort,
  }: {
    categoryIds?: string[];
    difficultyScores?: DifficultyScore[];
    sort?: CertificateSort;
  } = {}) => {
    const nextParams = new URLSearchParams(searchParams);

    if (categoryIds.length > 0) {
      nextParams.set("category", categoryIds.join(","));
    } else {
      nextParams.delete("category");
    }

    if (difficultyScores.length > 0) {
      nextParams.set("difficulty", difficultyScores.join(","));
    } else {
      nextParams.delete("difficulty");
    }

    if (sort === "recommended") {
      nextParams.delete("sort");
    } else {
      nextParams.set("sort", sort);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const toggleCategory = (categoryId: string) => {
    const nextCategories = selectedCategoryIds.includes(categoryId)
      ? selectedCategoryIds.filter((id) => id !== categoryId)
      : [...selectedCategoryIds, categoryId];
    updateUrlState({ categoryIds: nextCategories });
  };

  const toggleDifficulty = (score: DifficultyScore) => {
    const nextScores = selectedDifficultyScores.includes(score)
      ? selectedDifficultyScores.filter((value) => value !== score)
      : [...selectedDifficultyScores, score];
    updateUrlState({ difficultyScores: nextScores });
  };

  const toggleCard = (credentialId: string) => {
    setFlippedCards((cards) =>
      cards.includes(credentialId)
        ? cards.filter((id) => id !== credentialId)
        : [...cards, credentialId]
    );
  };

  const openClip = (credential: LibraryCredential) => {
    const segment = credentialSegments[credential.videoSegmentId];
    if (!segment) {
      toast.info(text.unavailable);
      return;
    }

    setActiveClip({
      title: credential.name,
      code: credential.code,
      segment,
    });
  };

  const swiperKey = visibleCredentials
    .map((credential) => credential.id)
    .join("-");

  return (
    <>
      <section className="container relative z-10 mt-8">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-secondary">
              {text.eyebrow}
            </p>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="text-3xl font-bold text-primary sm:text-4xl">
                {text.title}
              </h2>
              <span className="text-sm font-bold text-lightGray">
                {text.count(visibleCredentials.length)}
              </span>
            </div>
          </div>

          {visibleCredentials.length > 0 && (
            <div className="hidden shrink-0 gap-2 lg:flex">
              <button
                aria-label={text.previous}
                onClick={() => swiper?.slidePrev()}
                className="flex size-11 items-center justify-center rounded-full border border-primary/20 text-primary transition hover:bg-primary hover:text-white"
              >
                {language === "ar" ? (
                  <ChevronRight size={19} />
                ) : (
                  <ChevronLeft size={19} />
                )}
              </button>
              <button
                aria-label={text.next}
                onClick={() => swiper?.slideNext()}
                className="flex size-11 items-center justify-center rounded-full border border-primary/20 text-primary transition hover:bg-primary hover:text-white"
              >
                {language === "ar" ? (
                  <ChevronLeft size={19} />
                ) : (
                  <ChevronRight size={19} />
                )}
              </button>
            </div>
          )}
        </div>

        <CredentialLibraryFilters
          selectedCategoryIds={selectedCategoryIds}
          selectedDifficultyScores={selectedDifficultyScores}
          selectedSort={selectedSort}
          resultCount={visibleCredentials.length}
          language={language}
          onToggleCategory={toggleCategory}
          onClearCategories={() => updateUrlState({ categoryIds: [] })}
          onToggleDifficulty={toggleDifficulty}
          onClearDifficulties={() =>
            updateUrlState({ difficultyScores: [] })
          }
          onClearAll={() =>
            updateUrlState({ categoryIds: [], difficultyScores: [] })
          }
          onSortChange={(sort) => updateUrlState({ sort })}
        />

        {isLoading ? (
          <div className="flex h-64 items-center justify-center rounded-[28px] bg-white">
            <Spinner />
          </div>
        ) : visibleCredentials.length === 0 ? (
          <div className="rounded-[28px] border border-black/5 bg-white px-6 py-16 text-center shadow-sm">
            <p className="text-lg font-bold text-primary">
              {text.empty}
            </p>
            <button
              type="button"
              onClick={() =>
                updateUrlState({ categoryIds: [], difficultyScores: [] })
              }
              className="mt-5 rounded-full bg-secondary px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#df4315]"
            >
              {text.clear}
            </button>
          </div>
        ) : (
          <>
            <Swiper
              key={`${language}-${swiperKey}`}
              onSwiper={setSwiper}
              modules={[Navigation]}
              spaceBetween={16}
              slidesPerView={1.15}
              breakpoints={{
                640: { slidesPerView: 2.2 },
                900: { slidesPerView: 3.2 },
                1200: { slidesPerView: 4 },
              }}
              className="!overflow-visible lg:[&_.swiper-slide]:!mr-0 lg:[&_.swiper-slide]:!w-auto lg:[&_.swiper-wrapper]:!grid lg:[&_.swiper-wrapper]:!grid-cols-4 lg:[&_.swiper-wrapper]:!gap-4 lg:[&_.swiper-wrapper]:!transform-none"
            >
              {visibleCredentials.map((credential) => (
                <SwiperSlide key={credential.id} className="!h-auto">
                  <CredentialLibraryCard
                    credential={credential}
                    flipped={flippedCards.includes(credential.id)}
                    hasVideo={Boolean(
                      credentialSegments[credential.videoSegmentId]
                    )}
                    onToggle={() => toggleCard(credential.id)}
                    onPlay={() => openClip(credential)}
                    language={language}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="mt-6 flex justify-center gap-2 lg:hidden">
              <button
                aria-label={text.previous}
                onClick={() => swiper?.slidePrev()}
                className="flex size-10 items-center justify-center rounded-full border border-primary/20 text-primary"
              >
                {language === "ar" ? (
                  <ChevronRight size={18} />
                ) : (
                  <ChevronLeft size={18} />
                )}
              </button>
              <button
                aria-label={text.next}
                onClick={() => swiper?.slideNext()}
                className="flex size-10 items-center justify-center rounded-full border border-primary/20 text-primary"
              >
                {language === "ar" ? (
                  <ChevronLeft size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </button>
            </div>
          </>
        )}
      </section>

      <CredentialVideoModal
        clip={activeClip}
        language={language}
        onClose={() => setActiveClip(null)}
      />
    </>
  );
};

export default CredentialLibrary;
